import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  DestroyRef,
  ViewChild,
  TemplateRef,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';
import { ConciliadoraService } from '../../conciliadora/services/conciliadora.service';
import { FlowMonitorService } from '../../conciliadora/services/flow-monitor.service';
import {
  ConciliadoraRegistro,
  ConciliadoraTipoConfig,
  ExecutarOpts,
  FlowContextoAcao,
  GRID_COLS,
  GridCol,
  MktpCfgOption,
} from '../../conciliadora/models/conciliadora.models';

@Component({
  standalone: false,
  selector: 'app-registro-detail',
  templateUrl: './registro-detail.component.html',
  styleUrls: ['./registro-detail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RegistroDetailComponent {
  private destroyRef = inject(DestroyRef);

  @Input() registro!: ConciliadoraRegistro;
  @Input() tiposConfig: ConciliadoraTipoConfig[] = [];
  @Input() nuconf: number | null = null;
  @Input() codigoCab: number | null = null;
  /** Data de baixa do cabecalho (vinda do pai). Disponibilizada ao fluxo. */
  @Input() dtBaixa: string | null = null;
  /** Lista de fluxos com flag newMode (vinda do componente pai). */
  @Input() configsMktpCfg: MktpCfgOption[] = [];
  /** Item 4: colunas visiveis (mesma selecao do grid). Vazio => mostra tudo. */
  @Input() colunasVisiveis: string[] = [];
  /** Rotulos custom das colunas (LABELSGRADE), pro form usar o mesmo texto do grid. */
  @Input() colLabels: Record<string, string> = {};
  /** Colunas ADICIONAIS (extras) que o usuario incluiu no grid via COLUNASGRADE.
   *  Renderizadas numa secao "Campos adicionais" pra o detalhe refletir a grade. */
  @Input() colunasExtras: GridCol[] = [];

  private static readonly GRID_KEYS = new Set<string>(GRID_COLS.map((c) => c.key));

  /** Rotulo de uma coluna extra: usa o label custom (colLabels) se houver. */
  extraLabel(c: GridCol): string {
    return this.colLabels?.[c.key] || c.label || c.key;
  }

  /** Valor bruto da coluna extra no registro (—' quando vazio). */
  extraValor(key: string): string {
    const v = (this.registro as unknown as Record<string, unknown>)?.[key];
    return v === null || v === undefined || v === '' ? '—' : String(v);
  }

  /** Campo visivel no form. Campos que NAO sao coluna de grid (detalhe rico: TID,
   *  cartao, taxas...) sempre aparecem; os que SAO coluna de grid respeitam a
   *  selecao do item 2 (se houver). */
  campoVisivel(key: string): boolean {
    if (!RegistroDetailComponent.GRID_KEYS.has(key)) return true;
    return !this.colunasVisiveis?.length || this.colunasVisiveis.includes(key);
  }

  @Output() registroUpdated = new EventEmitter<void>();

  processing = false;

  /** Converte a data da baixa para AAAAMMDD (formato aceito pelo ERP). Robusto a
   * ISO (yyyy-MM-dd...) e dd/MM/yyyy; retorna null se nao reconhecer. */
  private formatDataAaaaMmDd(v: string | null | undefined): string | null {
    if (!v) return null;
    const s = String(v).trim();
    let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[1]}${m[2]}${m[3]}`;
    m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
    if (m) return `${m[3]}${m[2]}${m[1]}`;
    return null;
  }

  private parseTipoParametros(json: string | null | undefined): Record<string, any> {
    if (!json) return {};
    try {
      const arr = JSON.parse(json) as Array<{ campo: string; valor: string }>;
      const out: Record<string, any> = {};
      for (const it of arr || []) {
        if (it?.campo) out[it.campo] = it.valor;
      }
      return out;
    } catch {
      return {};
    }
  }

  constructor(
    private svc: ConciliadoraService,
    private cdr: ChangeDetectorRef,
    private msg: NzMessageService,
    private modal: NzModalService,
    private flowMonitor: FlowMonitorService
  ) {}

  get tipoConfig(): ConciliadoraTipoConfig | undefined {
    return this.tiposConfig.find((t) => t.TIPOS === this.registro?.TIPO);
  }

  get liberadoColor(): string {
    if (!this.registro?.LIBERADO) return 'default';
    if (this.registro.LIBERADO === 'Liberado') return 'green';
    if (this.registro.LIBERADO === 'Valor Divergente') return 'orange';
    if (this.registro.LIBERADO.startsWith('Erro')) return 'red';
    return 'default';
  }

  /** Motivo da decisao de reprocessar um registro divergente (item 1). */
  reprocMotivo = '';
  @ViewChild('reprocDivTpl') reprocDivTpl!: TemplateRef<unknown>;

  /** Registro esta divergente? Prefere STATUSCON/STATUSEFETIVO; cai pro LIBERADO. */
  get isDivergente(): boolean {
    const r = this.registro as (ConciliadoraRegistro & { STATUSEFETIVO?: string | null }) | undefined;
    if (!r) return false;
    return r.STATUSCON === 'D' || r.STATUSEFETIVO === 'D'
      || (!!r.LIBERADO && /divergente/i.test(r.LIBERADO));
  }

  onReprocessar(): void {
    const tc = this.tipoConfig;
    if (!tc?.IDCONF) {
      this.msg.warning('Nenhuma configuração (IDCONF) definida para o tipo ' + this.registro.TIPO);
      return;
    }

    // Registro DIVERGENTE: exige decisao explicita + motivo obrigatorio (historico
    // da decisao). O motivo + a flag vao pro fluxo (__forcarReprocesso/__motivo).
    if (this.isDivergente) {
      this.reprocMotivo = '';
      this.cdr.markForCheck();
      this.modal.confirm({
        nzTitle: 'Reprocessar registro DIVERGENTE?',
        nzContent: this.reprocDivTpl,
        nzOkText: 'Reprocessar mesmo assim',
        nzOkDanger: true,
        nzCancelText: 'Não',
        nzOnOk: () => {
          const motivo = this.reprocMotivo.trim();
          if (motivo.length < 5) {
            this.msg.warning('Informe o motivo da decisão (mínimo 5 caracteres).');
            return false;
          }
          this.dispatchReprocesso(tc, true, motivo);
          return true;
        },
      });
      return;
    }

    this.modal.confirm({
      nzTitle: 'Reprocessar Registro',
      nzContent: `Reprocessar registro #${this.registro.IDREGISTRO} via configuração #${tc.IDCONF}?`,
      nzOkText: 'Reprocessar',
      nzCancelText: 'Cancelar',
      nzOnOk: () => this.dispatchReprocesso(tc, false),
    });
  }

  /** Dispara o reprocesso. `forcar`+`motivo` so vem preenchidos no caso divergente. */
  private dispatchReprocesso(tc: ConciliadoraTipoConfig, forcar: boolean, motivo?: string): void {
    this.processing = true;
    this.cdr.markForCheck();

    const dpara = this.parseTipoParametros(tc.PARAMETROS);
    const params: Record<string, any> = {
      IDREGISTRO: this.registro.IDREGISTRO,
      CODIGO: this.registro.CODIGO,
      TIPO: this.registro.TIPO,
      VLRTOT: this.registro.VLRTOT,
      VLRLIQ: this.registro.VLRLIQ,
      NSU: this.registro.NSU,
      NOTA: this.registro.NOTA,
      PEDIDO: this.registro.PEDIDO,
      NUCONF: this.nuconf,
      DTBAIXA: this.dtBaixa ?? null,
      DTBAIXA_AAAAMMDD: this.formatDataAaaaMmDd(this.dtBaixa),
      ...dpara,
    };
    if (forcar) {
      // Fluxo baixa mesmo divergente e registra a decisao (MKTPREGCONCHIST) via __motivo.
      params['__forcarReprocesso'] = true;
      params['__motivo'] = motivo ?? '';
    }

    const opts: ExecutarOpts = {
      newMode: !!this.configsMktpCfg.find((c) => c.IDCONF === tc.IDCONF && c.newMode),
      contextoAcao: 'REPROCESSAR' as FlowContextoAcao,
      metadados: dpara,
      nuconf: this.nuconf,
      codigoCab: this.codigoCab,
    };

    // NEWMODE: dispara fluxo assincrono e abre o monitor (drawer pai cuida do drawer).
    if (opts.newMode) {
      this.flowMonitor.start(
        tc.IDCONF!,
        [params],
        opts,
        `Reprocessar registro #${this.registro.IDREGISTRO}`,
        'REPROCESSAR'
      );
      this.processing = false;
      this.msg.info('Execucao iniciada. Acompanhe pelo monitor de fluxo.');
      this.registroUpdated.emit();
      this.cdr.markForCheck();
      return;
    }

    this.svc
      .executarConfiguracao(tc.IDCONF!, params, opts)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.processing = false;
          this.msg.success('Registro reprocessado');
          this.registroUpdated.emit();
          this.cdr.markForCheck();
        },
        error: () => {
          this.processing = false;
          this.msg.error('Erro ao reprocessar');
          this.cdr.markForCheck();
        },
      });
  }

  onLiberarManual(): void {
    this.modal.confirm({
      nzTitle: 'Liberar manualmente',
      nzContent: `Confirmar liberação manual do registro #${this.registro.IDREGISTRO}? Esta ação marca o registro como "Liberado" sem executar o fluxo.`,
      nzOkText: 'Liberar',
      nzCancelText: 'Cancelar',
      nzOnOk: () => {
        this.processing = true;
        this.cdr.markForCheck();
        this.svc
          .saveRegistro({ IDREGISTRO: this.registro.IDREGISTRO, LIBERADO: 'Liberado' })
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.registro.LIBERADO = 'Liberado';
              this.processing = false;
              this.msg.success('Registro liberado manualmente');
              this.registroUpdated.emit();
              this.cdr.markForCheck();
            },
            error: () => {
              this.processing = false;
              this.msg.error('Erro ao liberar');
              this.cdr.markForCheck();
            },
          });
      },
    });
  }
}
